import { get } from "svelte/store";
import { writable } from "svelte/store";
import { userStore } from "../stores/user-store.js";
import { getUserByOID, getElasticSearchKey } from "../api/data-handler.js";
import { logInfo, logError, logAuthAttempt, logAuthSuccess, logAuthError, logTokenExpiration, logSignOut } from "../logging/log-handler.js";
import { v4 as uuidv4 } from "uuid";
import { navigateTo } from "../routing/route-action.js";
import { ROUTE } from "../../constants/route-constants.js";
import { getTokenExpiration as getTokenExpirationFromJWT } from "../../utils/jwt-utils.js";

const initialAuth = localStorage.getItem("auth") === "true";

export const isAuthenticated = writable<boolean>(initialAuth);
export const redirectUrl = writable<string>("/");
export const authToken = writable<string | null>(localStorage.getItem("authToken"));
export const sessionId = writable<string | null>(localStorage.getItem("sessionId"));

export async function handleSuccessfulAuth(object_id: string, token: string): Promise<boolean> {
  // Log authentication attempt
  logAuthAttempt("azure-ad", { oid: object_id });

  try {
    // Generate new session ID - stored in localStorage for current session
    const newSessionId = uuidv4();
    sessionId.set(newSessionId);
    localStorage.setItem("sessionId", newSessionId);

    // Store sensitive auth data in localStorage first
    localStorage.setItem("authToken", token);
    localStorage.setItem("authToken", token);
    authToken.set(token);

    // Get and store token expiration from JWT token directly
    try {
      const expiration = getTokenExpirationFromJWT(token);
      if (!expiration) {
        throw new Error("Could not extract expiration from token");
      }

      // Store original Unix timestamp for comparisons
      localStorage.setItem("tokenExpiration", expiration.toString());

      // Store human readable format in UTC
      const expirationDate = new Date(expiration * 1000);
      const readableExpiration = expirationDate.toISOString();
      localStorage.setItem("tokenExpirationReadable", readableExpiration);

      logInfo("Token expiration extracted from JWT", "auth-handler", {}, { readableExpiration });
    } catch (error) {
      logAuthError(error instanceof Error ? error : new Error("Failed to extract token expiration"), "azure-ad", { oid: object_id });
      return false;
    }

    // Now that we have the token stored, we can make authenticated requests
    // Get user data first, which now includes user_type, user_role and claims
    const userData = await getUserByOID(object_id);
    if (!userData || !userData.user) {
      logAuthError(new Error("User not found in database"), "azure-ad", {
        oid: object_id,
        errorMessage: "User not found in database",
      });
      return false;
    }

    // Store persistent data in localStorage
    localStorage.setItem("auth", "true");
    localStorage.setItem("object_id", object_id);

    // Store session-specific data in localStorage
    localStorage.setItem("auth", "true");

    // Store the user in userStore with the session ID
    userStore.setUser(userData.user, newSessionId);

    // Get elastic search key - this is required for the app to function
    try {
      const elasticSearchKey = await getElasticSearchKey();
      if (!elasticSearchKey) {
        logAuthError(new Error("Failed to get elastic search key"), "azure-ad", { oid: object_id });
        await handleSignOut();
        return false;
      }
      localStorage.setItem("elasticSearchKey", elasticSearchKey);
      sessionStorage.setItem("elasticSearchKey", elasticSearchKey);
    } catch (error) {
      logAuthError(new Error("Failed to get elastic search key"), "azure-ad", {
        oid: object_id,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      });
      await handleSignOut();
      return false;
    }

    // Log successful authentication with user details
    logAuthSuccess("azure-ad", {
      oid: object_id,
      userType: userData.user.user_type,
      userRole: userData.user.user_role,
    });

    isAuthenticated.set(true);
    return true;
  } catch (error) {
    logAuthError(error instanceof Error ? error : new Error("Post-authentication error"), "azure-ad", {
      oid: object_id,
      errorMessage: error instanceof Error ? error.message : "Unknown error",
    });
    await handleSignOut();
    return false;
  }
}

export async function handleSignOut(): Promise<void> {
  try {
    // Get user info before clearing for logging
    const currentUser = get(userStore);
    const oid = localStorage.getItem("object_id");

    // Clear persistent storage
    localStorage.removeItem("auth");
    localStorage.removeItem("object_id");

    // Clear sensitive session data
    localStorage.removeItem("auth");
    localStorage.removeItem("authToken");
    localStorage.removeItem("elasticSearchKey");
    localStorage.removeItem("sessionId");

    // Clear stores
    authToken.set(null);
    isAuthenticated.set(false);
    sessionId.set(null);
    userStore.clearUser();

    // Log sign out with user context
    logSignOut({
      oid,
      userType: currentUser?.user?.user_type,
      userRole: currentUser?.user?.user_role,
    });
  } catch (error) {
    logError(error instanceof Error ? error : new Error("Error during sign out"), "handleSignOut", undefined);
  }
}

export async function handleTokenExpiration(): Promise<void> {
  // Store current route for redirect after login
  const currentPath = window.location.pathname + window.location.search;
  localStorage.setItem("redirectAfterLogin", currentPath);

  // Log token expiration
  const oid = localStorage.getItem("object_id");
  logTokenExpiration({
    oid,
    redirectUrl: currentPath,
  });

  await handleSignOut();
  navigateTo(ROUTE.LOGIN);
}
